import { CheckMenuItem, Menu, MenuItem, PredefinedMenuItem, Submenu } from "@tauri-apps/api/menu";
import { uiStore } from "@/editor/store";
import { CURVA_OPEN_ABOUT_EVENT } from "@/editor/shell/aboutEvents";
import { listDesktopRecents } from "@/io/desktopConfig";
import { requestWindowClose } from "@/io/platform";

export interface NativeMenuHandlers {
  /** Run a `createEditorActions` id (File / Edit / View editor actions). */
  runAction: (actionId: string) => void;
  requestComponentImport: () => void;
  requestScriptImport: () => void;
  requestScriptImportAndReplace: () => void;
  requestAddEditorScripts: () => void;
  requestReplaceEditorScripts: () => void;
  clearEditorScripts: () => void;
  openEditorScriptsDocument: () => void;
  downloadEditorScriptsDocument: () => void;
  exportSelectedFramesPdf: () => void;
  openRecentPath: (path: string) => void;
  clearRecents: () => void;
}

function isMacOs(): boolean {
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform)
    || /Mac OS/.test(navigator.userAgent);
}

function basename(path: string): string {
  const parts = path.split(/[/\\]/);
  return parts.at(-1) || path;
}

let openRecentSubmenu: Submenu | undefined;
let openRecentHandlersRef: { current: NativeMenuHandlers } | undefined;

async function buildOpenRecentItems(
  handlersRef: { current: NativeMenuHandlers },
): Promise<Array<MenuItem | PredefinedMenuItem>> {
  const recents = await listDesktopRecents();
  const items: Array<MenuItem | PredefinedMenuItem> = [];
  if (recents.length === 0) {
    items.push(await MenuItem.new({
      id: "file.openRecent.empty",
      text: "No Recent Documents",
      enabled: false,
      action: () => undefined,
    }));
  } else {
    for (const [index, entry] of recents.entries()) {
      const path = entry.path;
      items.push(await MenuItem.new({
        id: `file.openRecent.${index}`,
        text: basename(path),
        action: () => {
          handlersRef.current.openRecentPath(path);
        },
      }));
    }
  }
  items.push(await PredefinedMenuItem.new({ item: "Separator" }));
  items.push(await MenuItem.new({
    id: "file.openRecent.clear",
    text: "Clear Menu",
    enabled: recents.length > 0,
    action: () => {
      handlersRef.current.clearRecents();
    },
  }));
  return items;
}

/** Rebuild File → Open Recent entries from disk (prunes missing paths). */
export async function refreshNativeRecentsMenu(): Promise<void> {
  if (!openRecentSubmenu || !openRecentHandlersRef) {
    return;
  }
  try {
    const existing = await openRecentSubmenu.items();
    for (const item of existing) {
      await openRecentSubmenu.remove(item);
    }
    const next = await buildOpenRecentItems(openRecentHandlersRef);
    await openRecentSubmenu.append(next);
  } catch (error) {
    console.warn("Failed to refresh Open Recent menu", error);
  }
}

/**
 * Install the desktop app menu. Accelerators for catalogued editor actions are owned here;
 * `stripNativeMenuOwnedShortcuts` must clear the matching keymap shortcuts to avoid double-fire.
 * HTML hamburger menu stays available for the same commands (click-only).
 */
export async function installNativeAppMenu(
  handlersRef: { current: NativeMenuHandlers },
): Promise<() => void> {
  const run = (actionId: string) => () => {
    handlersRef.current.runAction(actionId);
  };

  openRecentHandlersRef = handlersRef;

  const aboutItem = await MenuItem.new({
    id: "app.about",
    text: "About Curva",
    action: () => {
      window.dispatchEvent(new Event(CURVA_OPEN_ABOUT_EVENT));
    },
  });

  openRecentSubmenu = await Submenu.new({
    id: "menu.file.openRecent",
    text: "Open Recent",
    items: await buildOpenRecentItems(handlersRef),
  });

  const fileSubmenu = await Submenu.new({
    id: "menu.file",
    text: "File",
    items: [
      await MenuItem.new({ id: "file.new", text: "New", accelerator: "CmdOrCtrl+N", action: run("file.new") }),
      await MenuItem.new({ id: "file.open", text: "Open…", accelerator: "CmdOrCtrl+O", action: run("file.open") }),
      openRecentSubmenu,
      await MenuItem.new({
        id: "file.importComponents",
        text: "Import Components…",
        action: () => {
          handlersRef.current.requestComponentImport();
        },
      }),
      await PredefinedMenuItem.new({ item: "Separator" }),
      await MenuItem.new({ id: "file.save", text: "Save", accelerator: "CmdOrCtrl+S", action: run("file.save") }),
      await MenuItem.new({
        id: "file.saveAs",
        text: "Save As…",
        accelerator: "CmdOrCtrl+Shift+S",
        action: run("file.saveAs"),
      }),
      await PredefinedMenuItem.new({ item: "Separator" }),
      await MenuItem.new({
        id: "file.exportPdf",
        text: "Export Selected Frames as PDF…",
        action: () => {
          handlersRef.current.exportSelectedFramesPdf();
        },
      }),
    ],
  });

  const scriptsSubmenu = await Submenu.new({
    id: "menu.scripts",
    text: "Scripts",
    items: [
      await MenuItem.new({
        id: "scripts.import",
        text: "Import Scripts…",
        action: () => {
          handlersRef.current.requestScriptImport();
        },
      }),
      await MenuItem.new({
        id: "scripts.importAndReplace",
        text: "Import and Replace Scripts…",
        action: () => {
          handlersRef.current.requestScriptImportAndReplace();
        },
      }),
      await PredefinedMenuItem.new({ item: "Separator" }),
      await MenuItem.new({
        id: "scripts.addEditor",
        text: "Add Editor Scripts…",
        action: () => {
          handlersRef.current.requestAddEditorScripts();
        },
      }),
      await MenuItem.new({
        id: "scripts.replaceEditor",
        text: "Replace Editor Scripts…",
        action: () => {
          handlersRef.current.requestReplaceEditorScripts();
        },
      }),
      await MenuItem.new({
        id: "scripts.clearEditor",
        text: "Clear Editor Scripts",
        action: () => {
          handlersRef.current.clearEditorScripts();
        },
      }),
      await MenuItem.new({
        id: "scripts.openEditor",
        text: "Open Editor Scripts Document",
        action: () => {
          handlersRef.current.openEditorScriptsDocument();
        },
      }),
      await MenuItem.new({
        id: "scripts.downloadEditor",
        text: "Download Editor Scripts Document…",
        action: () => {
          handlersRef.current.downloadEditorScriptsDocument();
        },
      }),
    ],
  });

  const editSubmenu = await Submenu.new({
    id: "menu.edit",
    text: "Edit",
    items: [
      await MenuItem.new({ id: "history.undo", text: "Undo", accelerator: "CmdOrCtrl+Z", action: run("history.undo") }),
      await MenuItem.new({
        id: "history.redo",
        text: "Redo",
        accelerator: "CmdOrCtrl+Shift+Z",
        action: run("history.redo"),
      }),
      await PredefinedMenuItem.new({ item: "Separator" }),
      await MenuItem.new({ id: "edit.cutSelection", text: "Cut", accelerator: "CmdOrCtrl+X", action: run("edit.cutSelection") }),
      await MenuItem.new({ id: "edit.copySelection", text: "Copy", accelerator: "CmdOrCtrl+C", action: run("edit.copySelection") }),
      await MenuItem.new({
        id: "edit.pasteSelection",
        text: "Paste",
        accelerator: "CmdOrCtrl+V",
        action: run("edit.pasteSelection"),
      }),
      await MenuItem.new({
        id: "edit.selectAll",
        text: "Select All",
        accelerator: "CmdOrCtrl+A",
        action: run("edit.selectAll"),
      }),
    ],
  });

  const debug = uiStore.canvasDebug.getValue();
  const snap = uiStore.canvasSnap.getValue();
  const animation = uiStore.animation.getValue();

  const showPixelGrid = await CheckMenuItem.new({
    id: "view.showPixelGrid",
    text: "Show Pixel Grid",
    checked: debug.showPixelGrid,
    action: () => {
      const next = !uiStore.canvasDebug.getValue().showPixelGrid;
      uiStore.patchCanvasDebug({ showPixelGrid: next });
      void showPixelGrid.setChecked(next);
    },
  });
  const showRulers = await CheckMenuItem.new({
    id: "view.toggleRulers",
    text: "Show Rulers",
    checked: debug.showRulers,
    accelerator: "Shift+R",
    action: () => {
      run("view.toggleRulers")();
      void showRulers.setChecked(uiStore.canvasDebug.getValue().showRulers);
    },
  });
  const showAnimPreview = await CheckMenuItem.new({
    id: "view.toggleAnimationPreviewWindow",
    text: "Show Animation Preview",
    checked: animation.previewWindowOpen,
    accelerator: "CmdOrCtrl+Shift+P",
    action: () => {
      run("view.toggleAnimationPreviewWindow")();
      void showAnimPreview.setChecked(uiStore.animation.getValue().previewWindowOpen);
    },
  });
  const allowScripts = await CheckMenuItem.new({
    id: "view.toggleAllowScripts",
    text: "Allow Scripts",
    checked: uiStore.allowScripts.getValue(),
    action: () => {
      run("view.toggleAllowScripts")();
      void allowScripts.setChecked(uiStore.allowScripts.getValue());
    },
  });
  const showScriptsDebug = await CheckMenuItem.new({
    id: "view.toggleScriptsDebugWindow",
    text: "Show Scripts Debug",
    checked: uiStore.scriptsDebugWindowOpen.getValue(),
    action: () => {
      run("view.toggleScriptsDebugWindow")();
      void showScriptsDebug.setChecked(uiStore.scriptsDebugWindowOpen.getValue());
    },
  });
  const snapPixel = await CheckMenuItem.new({
    id: "view.snapToPixelGrid",
    text: "Snap to Pixel Grid",
    checked: snap.snapToPixelGrid,
    action: () => {
      const next = !uiStore.canvasSnap.getValue().snapToPixelGrid;
      uiStore.patchCanvasSnap({ snapToPixelGrid: next });
      void snapPixel.setChecked(next);
    },
  });
  const snapObjects = await CheckMenuItem.new({
    id: "view.snapToObjects",
    text: "Snap to Objects",
    checked: snap.snapToObjects,
    action: () => {
      const next = !uiStore.canvasSnap.getValue().snapToObjects;
      uiStore.patchCanvasSnap({ snapToObjects: next });
      void snapObjects.setChecked(next);
    },
  });
  const snapGuides = await CheckMenuItem.new({
    id: "view.snapToGuides",
    text: "Snap to Guides",
    checked: snap.snapToGuides,
    action: () => {
      const next = !uiStore.canvasSnap.getValue().snapToGuides;
      uiStore.setSnapToGuides(next);
      void snapGuides.setChecked(next);
    },
  });
  const snapGeometry = await CheckMenuItem.new({
    id: "view.snapToGeometry",
    text: "Snap to Geometry",
    checked: snap.snapToGeometry,
    action: () => {
      const next = !uiStore.canvasSnap.getValue().snapToGeometry;
      uiStore.patchCanvasSnap({ snapToGeometry: next });
      void snapGeometry.setChecked(next);
    },
  });
  const assistedPencil = await CheckMenuItem.new({
    id: "view.assistedPencilTracing",
    text: "Assisted Pencil Tracing",
    checked: snap.assistedPencilTracing,
    action: () => {
      const next = !uiStore.canvasSnap.getValue().assistedPencilTracing;
      uiStore.patchCanvasSnap({ assistedPencilTracing: next });
      void assistedPencil.setChecked(next);
    },
  });

  const viewSubmenu = await Submenu.new({
    id: "menu.view",
    text: "View",
    items: [
      showPixelGrid,
      showRulers,
      showAnimPreview,
      allowScripts,
      showScriptsDebug,
      await PredefinedMenuItem.new({ item: "Separator" }),
      snapPixel,
      snapObjects,
      snapGuides,
      snapGeometry,
      await PredefinedMenuItem.new({ item: "Separator" }),
      assistedPencil,
      await PredefinedMenuItem.new({ item: "Separator" }),
      await MenuItem.new({
        id: "view.resetZoom",
        text: "Zoom to 100%",
        accelerator: "CmdOrCtrl+0",
        action: run("view.resetZoom"),
      }),
    ],
  });

  const syncChecks = (): void => {
    const nextDebug = uiStore.canvasDebug.getValue();
    const nextSnap = uiStore.canvasSnap.getValue();
    const nextAnimation = uiStore.animation.getValue();
    void showPixelGrid.setChecked(nextDebug.showPixelGrid);
    void showRulers.setChecked(nextDebug.showRulers);
    void showAnimPreview.setChecked(nextAnimation.previewWindowOpen);
    void allowScripts.setChecked(uiStore.allowScripts.getValue());
    void showScriptsDebug.setChecked(uiStore.scriptsDebugWindowOpen.getValue());
    void snapPixel.setChecked(nextSnap.snapToPixelGrid);
    void snapObjects.setChecked(nextSnap.snapToObjects);
    void snapGuides.setChecked(nextSnap.snapToGuides);
    void snapGeometry.setChecked(nextSnap.snapToGeometry);
    void assistedPencil.setChecked(nextSnap.assistedPencilTracing);
  };

  const unsubDebug = uiStore.canvasDebug.subscribe(syncChecks);
  const unsubSnap = uiStore.canvasSnap.subscribe(syncChecks);
  const unsubAnimation = uiStore.animation.subscribe(syncChecks);
  const unsubAllowScripts = uiStore.allowScripts.subscribe(syncChecks);
  const unsubScriptsDebug = uiStore.scriptsDebugWindowOpen.subscribe(syncChecks);

  const submenus: Submenu[] = [];
  if (isMacOs()) {
    submenus.push(await Submenu.new({
      id: "menu.app",
      text: "Curva",
      items: [
        aboutItem,
        await PredefinedMenuItem.new({ item: "Separator" }),
        await PredefinedMenuItem.new({ item: "Hide" }),
        await PredefinedMenuItem.new({ item: "HideOthers" }),
        await PredefinedMenuItem.new({ item: "ShowAll" }),
        await PredefinedMenuItem.new({ item: "Separator" }),
        await MenuItem.new({
          id: "app.quit",
          text: "Quit Curva",
          accelerator: "CmdOrCtrl+Q",
          action: () => {
            void requestWindowClose();
          },
        }),
      ],
    }));
  }

  submenus.push(fileSubmenu, editSubmenu, scriptsSubmenu, viewSubmenu);

  if (!isMacOs()) {
    submenus.push(await Submenu.new({
      id: "menu.help",
      text: "Help",
      items: [aboutItem],
    }));
  }

  const menu = await Menu.new({ items: submenus });
  if (isMacOs()) {
    await menu.setAsAppMenu();
  } else {
    await menu.setAsWindowMenu();
  }

  return () => {
    unsubDebug();
    unsubSnap();
    unsubAnimation();
    unsubAllowScripts();
    unsubScriptsDebug();
    openRecentSubmenu = undefined;
    openRecentHandlersRef = undefined;
  };
}
